import React from 'react';
import { useDispatch } from 'react-redux';

import {
  SetupSignaturePageHeightImageAction,
  SetupSignaturePageImageAction,
} from 'src/store/signaturePage/actions';
import { SignaturePageImageData } from 'src/store/signaturePage/types';

import { SignaturePageImage } from './SignaturePageImage';

interface SignaturePageImageContainerProps {
  pageImage: SignaturePageImageData;
}

export const SignaturePageImageContainer: React.FC<
  SignaturePageImageContainerProps
> = ({ pageImage }) => {
  const dispatch = useDispatch();

  const pageImgElement = React.useRef<HTMLDivElement>(null);
  const imageYPos = React.useRef<number | null>(null);

  const updatePageImageYPosition = React.useCallback(() => {
    if (!pageImgElement.current) return;

    const y = pageImgElement.current.offsetTop;
    if (y !== imageYPos.current) {
      imageYPos.current = y;
      dispatch(SetupSignaturePageHeightImageAction(pageImage.key, y));
    }
  }, [dispatch, pageImage.key]);

  React.useEffect(() => {
    updatePageImageYPosition();

    const pageElement = pageImgElement.current;
    if (!pageElement || typeof ResizeObserver === 'undefined') {
      const positionTimer = window.setInterval(updatePageImageYPosition, 500);

      window.addEventListener('resize', updatePageImageYPosition);

      return () => {
        clearInterval(positionTimer);
        window.removeEventListener('resize', updatePageImageYPosition);
      };
    }

    const resizeObserver = new ResizeObserver(updatePageImageYPosition);
    resizeObserver.observe(pageElement);
    if (pageElement.parentElement) {
      resizeObserver.observe(pageElement.parentElement);
    }

    window.addEventListener('resize', updatePageImageYPosition);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updatePageImageYPosition);
    };
  }, [updatePageImageYPosition]);

  const handlePageImageLoad = (
    event: React.SyntheticEvent<HTMLImageElement>,
  ) => {
    const { offsetTop: y, offsetHeight } = event.currentTarget;
    const pageData = {
      ...pageImage,
      loaded: true,
      element: { y, offsetHeight },
    };
    dispatch(SetupSignaturePageImageAction(pageData));
    updatePageImageYPosition();
  };

  return (
    <SignaturePageImage
      ref={pageImgElement}
      pageImage={pageImage}
      handlePageImageLoad={handlePageImageLoad}
    />
  );
};
